import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftOutlined, SearchOutlined } from '@ant-design/icons';
import { Flex, Typography } from 'antd';
import { SbButton, SbSelect, SbSpin, SbTooltip } from 'uiCore';
import useAuthStore from 'Store/useAuthStore';
import useEntitlementsStore from 'Store/useEntitlementsStore';
import {
  JD_ARCHIVE_SEARCHABLE_FIELDS,
  JD_ARCHIVE_SEARCH_PLACEHOLDER,
  JD_ARCHIVE_SEARCH_TOOLTIP,
} from 'constants/jdArchive.constants';
import useJdArchive from './useJdArchive';
import JdArchiveTable from './JdArchiveTable';
import useAnalytics from "Hooks/useAnalytics";
import { ANALYTICS_EVENTS } from "Utils/analytics";
import {
  PageContainer,
  HeaderSection,
  HeaderLeft,
  BackButton,
  TitleGroup,
  PageTitle,
  PageSubtitle,
} from '../CompensationManagement/CompensationManagement.styles';
import {
  JdArchiveContentSectionStyled,
  JdArchiveLoaderStyled,
  JdArchiveSearchWrapperStyled,
} from './JdArchive.styles';

const normalize = (value) => String(value ?? '').trim().toLowerCase();

const ANALYTICS_FROM = "admin_setup_jd_archive";
const ANALYTICS_EVENT = ANALYTICS_EVENTS.ADMIN_SETUP.VIEWED;

const JdArchive = () => {
  const track = useAnalytics(ANALYTICS_FROM);
  useEffect(() => {
    track(ANALYTICS_EVENT);
  }, [track]);
  const navigate = useNavigate();
  const email = useAuthStore((s) => s.email);
  const { entitlementsData, profileRole } = useEntitlementsStore();
  const [hasFetchedOnce, setHasFetchedOnce] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const isHRAdmin = profileRole === 'HR Admin';

  const { loading, rows, fetchData, archiveJd, actioningId } = useJdArchive(email, entitlementsData, profileRole);

  const handleArchive = (row) => {
    track(ANALYTICS_EVENTS.ADMIN_SETUP.JD_ARCHIVED, { jobId: row?.job_id ?? row?.sb_unique_id });
    return archiveJd(row);
  };

  useEffect(() => {
    if (!email || !isHRAdmin) return;
    fetchData().finally(() => setHasFetchedOnce(true));
  }, [email, isHRAdmin, fetchData]);

  // Dropdown suggestions: every distinct value across the searchable fields,
  // sorted numeric-first to match the table's column filters.
  const searchOptions = useMemo(() => {
    const unique = new Set();
    (rows || []).forEach((row) =>
      JD_ARCHIVE_SEARCHABLE_FIELDS.forEach((field) => {
        const value = row?.[field];
        if (value && typeof value === 'string' && value.trim() && value.trim() !== '--') unique.add(value);
      }),
    );
    return Array.from(unique)
      .sort((a, b) => {
        const aNum = /^\d/.test(a);
        const bNum = /^\d/.test(b);
        if (aNum && !bNum) return -1;
        if (!aNum && bNum) return 1;
        return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
      })
      .map((value) => ({ value, label: value }));
  }, [rows]);

  const filteredRows = useMemo(() => {
    const term = normalize(searchTerm);
    if (!term) return rows;
    return (rows || []).filter((row) =>
      JD_ARCHIVE_SEARCHABLE_FIELDS.some((field) => normalize(row?.[field]).includes(term)),
    );
  }, [rows, searchTerm]);

  if (!isHRAdmin) {
    return (
      <PageContainer>
        <Flex align="center" justify="center" style={{ minHeight: '60vh' }}>
          <Typography.Text type="secondary">
            Access to JD Archive is restricted to HR Admin users only.
          </Typography.Text>
        </Flex>
      </PageContainer>
    );
  }

  const showInitialLoader = loading && !hasFetchedOnce;

  return (
    <PageContainer>
      <HeaderSection>
        <HeaderLeft>
          <BackButton onClick={() => navigate('/admin-setup')} aria-label="Back to Admin Setup">
            <ArrowLeftOutlined />
          </BackButton>
          <TitleGroup>
            <PageTitle>JD Archive</PageTitle>
            <PageSubtitle>Archive job descriptions that are no longer active — this cannot be undone</PageSubtitle>
          </TitleGroup>
        </HeaderLeft>

        <SbTooltip content={JD_ARCHIVE_SEARCH_TOOLTIP}>
          <JdArchiveSearchWrapperStyled>
            <SbSelect
              options={searchOptions}
              value={searchTerm || undefined}
              suffixIcon={<SbButton buttonType="primary" size="small" icon={<SearchOutlined />} />}
              onChange={(value) => setSearchTerm(value ?? '')}
              placeholder={JD_ARCHIVE_SEARCH_PLACEHOLDER}
              allowClear
              showSearch
              notFoundContent="No matching results"
              listHeight={300}
            />
          </JdArchiveSearchWrapperStyled>
        </SbTooltip>
      </HeaderSection>

      {showInitialLoader ? (
        <JdArchiveLoaderStyled>
          <SbSpin size="large" />
        </JdArchiveLoaderStyled>
      ) : (
        <JdArchiveContentSectionStyled>
          <JdArchiveTable rows={filteredRows} loading={loading} onArchive={handleArchive} actioningId={actioningId} />
        </JdArchiveContentSectionStyled>
      )}
    </PageContainer>
  );
};

export default JdArchive;
